import React, { useCallback, useEffect, useMemo, useState } from "react";
import { parseApiError } from "shared/api/client/errorParser";
import {
  Organization,
  OrganizationDetailsPayload,
  OrganizationMember,
  createOrganization,
  getMyOrganizations,
  getOrganizationMembers,
  inviteOrganizationMember,
  removeOrganizationMember,
  updateOrganization,
} from "../api/account.api";
import "./OrganizationTeamPanel.css";

type Notice = { type: "success" | "error"; text: string } | null;

type OrgFormState = Required<OrganizationDetailsPayload>;

const EMPTY_FORM: OrgFormState = {
  name: "",
  website: "",
  country: "",
  bio: "",
  youTubeChannel: "",
  xcom: "",
  instagram: "",
  tikTok: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toForm(org: Organization): OrgFormState {
  return {
    name: org.name,
    website: org.website,
    country: org.country,
    bio: org.bio,
    youTubeChannel: org.youTubeChannel,
    xcom: org.xcom,
    instagram: org.instagram,
    tikTok: org.tikTok,
  };
}

type Props = {
  currentUserId?: number | string | null;
  currentUserEmail?: string | null;
  onOrganizationsChanged?: (organizations: Organization[]) => void;
};

const OrganizationTeamPanel: React.FC<Props> = ({
  currentUserId,
  currentUserEmail,
  onOrganizationsChanged,
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice>(null);

  const [form, setForm] = useState<OrgFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<OrgFormState>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"Admin" | "Member">("Member");
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const fail = useCallback((err: unknown, fallback: string) => {
    setNotice({ type: "error", text: parseApiError(err, fallback) });
  }, []);

  const loadOrganizations = useCallback(
    async (preferredId?: number | null) => {
      try {
        const list = await getMyOrganizations();
        setOrganizations(list);
        onOrganizationsChanged?.(list);
        setSelectedId((current) => {
          const wanted = preferredId ?? current;
          if (wanted && list.some((org) => org.id === wanted)) return wanted;
          return list[0]?.id ?? null;
        });
      } catch (err) {
        fail(err, "Could not load your organizations.");
      } finally {
        setLoading(false);
      }
    },
    [fail, onOrganizationsChanged]
  );

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const selected = useMemo(
    () => organizations.find((org) => org.id === selectedId) ?? null,
    [organizations, selectedId]
  );

  useEffect(() => {
    setForm(selected ? toForm(selected) : EMPTY_FORM);
  }, [selected]);

  useEffect(() => {
    if (!selected) {
      setMembers([]);
      return;
    }
    let active = true;
    setMembersLoading(true);
    getOrganizationMembers(selected.id)
      .then((list) => {
        if (active) setMembers(list);
      })
      .catch((err) => {
        if (active) fail(err, "Could not load organization members.");
      })
      .finally(() => {
        if (active) setMembersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selected, fail]);

  const canManage = useMemo(() => {
    if (!selected) return false;
    if (currentUserId !== undefined && currentUserId !== null && Number(currentUserId) === selected.ownerUserId) {
      return true;
    }
    const me = members.find(
      (m) =>
        (currentUserId !== undefined && currentUserId !== null && Number(currentUserId) === m.userId && m.userId !== 0) ||
        (currentUserEmail && m.email.toLowerCase() === currentUserEmail.toLowerCase())
    );
    return Boolean(me) && ["owner", "admin"].includes(me!.role.toLowerCase());
  }, [selected, members, currentUserId, currentUserEmail]);

  const hasChanges = useMemo(
    () => (selected ? JSON.stringify(toForm(selected)) !== JSON.stringify(form) : false),
    [selected, form]
  );

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const name = form.name.trim();
    if (!name || name.length > 120) {
      setNotice({ type: "error", text: "Organization name is required (max 120 characters)." });
      return;
    }
    setSaving(true);
    try {
      await updateOrganization(selected.id, { ...form, name });
      setNotice({ type: "success", text: `Organization "${name}" updated.` });
      await loadOrganizations(selected.id);
    } catch (err) {
      fail(err, "Could not update the organization.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = createForm.name.trim();
    if (!name || name.length > 120) {
      setNotice({ type: "error", text: "Organization name is required (max 120 characters)." });
      return;
    }
    setCreating(true);
    try {
      const created = await createOrganization({ ...createForm, name });
      setCreateForm(EMPTY_FORM);
      setShowCreate(false);
      setNotice({ type: "success", text: `Organization "${created.name}" created.` });
      await loadOrganizations(created.id);
    } catch (err) {
      fail(err, "Could not create the organization.");
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const email = inviteEmail.trim();
    if (!EMAIL_PATTERN.test(email)) {
      setNotice({ type: "error", text: "Enter a valid email address to invite." });
      return;
    }
    setInviting(true);
    try {
      const member = await inviteOrganizationMember(selected.id, { email, role: inviteRole });
      setMembers((prev) => [...prev.filter((m) => m.id !== member.id), member]);
      setInviteEmail("");
      setNotice({ type: "success", text: `Invitation sent to ${email}.` });
    } catch (err) {
      fail(err, "Could not invite the collaborator.");
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (member: OrganizationMember) => {
    if (!selected) return;
    setRemovingId(member.id);
    try {
      await removeOrganizationMember(selected.id, member.id);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      setNotice({ type: "success", text: `${member.email} removed from ${selected.name}.` });
    } catch (err) {
      fail(err, "Could not remove the member.");
    } finally {
      setRemovingId(null);
    }
  };

  const renderOrgFields = (
    state: OrgFormState,
    update: (patch: Partial<OrgFormState>) => void,
    idPrefix: string
  ) => (
    <>
      <div className="org-row">
        <label className="org-field" htmlFor={`${idPrefix}-name`}>
          Organization name
          <input
            id={`${idPrefix}-name`}
            value={state.name}
            maxLength={120}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="e.g. Poetics XR Studio"
          />
        </label>
        <label className="org-field" htmlFor={`${idPrefix}-website`}>
          Website
          <input
            id={`${idPrefix}-website`}
            value={state.website}
            onChange={(e) => update({ website: e.target.value })}
            placeholder="https://"
          />
        </label>
        <label className="org-field org-field--short" htmlFor={`${idPrefix}-country`}>
          Country
          <input
            id={`${idPrefix}-country`}
            value={state.country}
            maxLength={8}
            onChange={(e) => update({ country: e.target.value.toUpperCase() })}
            placeholder="GR"
          />
        </label>
      </div>
      <label className="org-field" htmlFor={`${idPrefix}-bio`}>
        Bio
        <textarea
          id={`${idPrefix}-bio`}
          value={state.bio}
          maxLength={2000}
          onChange={(e) => update({ bio: e.target.value })}
          placeholder="What does your studio create?"
        />
      </label>
      <div className="org-row">
        <label className="org-field" htmlFor={`${idPrefix}-youtube`}>
          YouTube
          <input
            id={`${idPrefix}-youtube`}
            value={state.youTubeChannel}
            onChange={(e) => update({ youTubeChannel: e.target.value })}
          />
        </label>
        <label className="org-field" htmlFor={`${idPrefix}-xcom`}>
          X
          <input
            id={`${idPrefix}-xcom`}
            value={state.xcom}
            onChange={(e) => update({ xcom: e.target.value })}
          />
        </label>
        <label className="org-field" htmlFor={`${idPrefix}-instagram`}>
          Instagram
          <input
            id={`${idPrefix}-instagram`}
            value={state.instagram}
            onChange={(e) => update({ instagram: e.target.value })}
          />
        </label>
        <label className="org-field" htmlFor={`${idPrefix}-tiktok`}>
          TikTok
          <input
            id={`${idPrefix}-tiktok`}
            value={state.tikTok}
            onChange={(e) => update({ tikTok: e.target.value })}
          />
        </label>
      </div>
    </>
  );

  return (
    <section className="org-team" aria-label="Organization and team">
      {notice && (
        <div className={`org-message ${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>
          {notice.text}
        </div>
      )}

      <div className="org-panel">
        <div className="org-panel-header">
          <h3>Organization</h3>
          <div className="org-panel-actions">
            {organizations.length > 1 && (
              <select
                aria-label="Select organization"
                value={selectedId ?? ""}
                onChange={(e) => setSelectedId(Number(e.target.value) || null)}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="org-btn secondary"
              onClick={() => setShowCreate((v) => !v)}
            >
              {showCreate ? "Cancel" : "New organization"}
            </button>
          </div>
        </div>

        {showCreate && (
          <form className="org-create" onSubmit={handleCreate} aria-label="Create organization">
            <h4>Create a new organization</h4>
            {renderOrgFields(createForm, (patch) => setCreateForm((p) => ({ ...p, ...patch })), "org-create")}
            <button type="submit" className="org-btn" disabled={creating}>
              {creating ? "Creating…" : "Create organization"}
            </button>
          </form>
        )}

        {loading ? (
          <p className="org-empty">Loading organizations…</p>
        ) : !selected ? (
          <p className="org-empty">
            You don’t belong to an organization yet. Create one to start inviting collaborators.
          </p>
        ) : (
          <form onSubmit={handleSave} aria-label="Organization details">
            <fieldset disabled={!canManage} className="org-fieldset">
              {renderOrgFields(form, (patch) => setForm((p) => ({ ...p, ...patch })), "org-edit")}
            </fieldset>
            {canManage ? (
              <div className="org-actions">
                <button type="submit" className="org-btn" disabled={saving || !hasChanges}>
                  {saving ? "Saving…" : "Save organization"}
                </button>
                <button
                  type="button"
                  className="org-btn secondary"
                  disabled={!hasChanges}
                  onClick={() => setForm(toForm(selected))}
                >
                  Reset
                </button>
              </div>
            ) : (
              <p className="org-empty">Only organization owners and admins can edit these details.</p>
            )}
          </form>
        )}
      </div>

      {selected && (
        <div className="org-panel">
          <h3>Team members</h3>
          {membersLoading ? (
            <p className="org-empty">Loading members…</p>
          ) : members.length === 0 ? (
            <p className="org-empty">No members yet.</p>
          ) : (
            <table className="org-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const isOwner =
                    member.role.toLowerCase() === "owner" ||
                    (member.userId !== 0 && member.userId === selected.ownerUserId);
                  return (
                    <tr key={member.id} data-testid="org-member">
                      <td>{member.email}</td>
                      <td>{member.role}</td>
                      <td>
                        <span className={`org-pill ${member.invitationSent ? "org-pill--invited" : "org-pill--active"}`}>
                          {member.label ?? (member.invitationSent ? "Invited" : "Active")}
                        </span>
                      </td>
                      {canManage && (
                        <td style={{ textAlign: "right" }}>
                          {!isOwner && (
                            <button
                              type="button"
                              className="org-btn danger small"
                              onClick={() => handleRemove(member)}
                              disabled={removingId === member.id}
                              aria-label={`Remove ${member.email}`}
                            >
                              {removingId === member.id ? "Removing…" : "Remove"}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {canManage && (
            <form className="org-invite" onSubmit={handleInvite} aria-label="Invite collaborator">
              <h4>Invite a collaborator</h4>
              <div className="org-row">
                <label className="org-field" htmlFor="org-invite-email">
                  Email
                  <input
                    id="org-invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="collaborator@studio.com"
                  />
                </label>
                <label className="org-field org-field--short" htmlFor="org-invite-role">
                  Role
                  <select
                    id="org-invite-role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value === "Admin" ? "Admin" : "Member")}
                  >
                    <option value="Member">Member</option>
                    <option value="Admin">Admin</option>
                  </select>
                </label>
                <button type="submit" className="org-btn" disabled={inviting}>
                  {inviting ? "Inviting…" : "Send invite"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
};

export default OrganizationTeamPanel;
